import { useCallback, useMemo, useState } from 'react';

import type {
  ColumnDef,
  ColumnOrder,
  ColumnPin,
  ColumnSizing,
  ColumnVisibility,
  TableBaseImplProps,
  SelectionMode,
  SortDirection,
  TableSort,
  TableSection,
} from './TableBaseImpl.types';

type ControlledStateOptions<T> = {
  value?: T;
  defaultValue: T;
  onChange?: (value: T) => void;
};

function useControlledState<T>({
  value,
  defaultValue,
  onChange,
}: ControlledStateOptions<T>) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) {
        setInternal(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [current, setValue] as const;
}

function createDefaultVisibility<T>(
  columns: ColumnDef<T>[],
  overrides?: ColumnVisibility,
) {
  const base = columns.reduce<ColumnVisibility>((acc, col) => {
    acc[col.id] = col.hidden ? false : true;
    return acc;
  }, {});

  return { ...base, ...(overrides ?? {}) };
}

function createDefaultSizing<T>(columns: ColumnDef<T>[]) {
  return columns.reduce<ColumnSizing>((acc, col) => {
    if (typeof col.width === 'number') {
      acc[col.id] = col.width;
    }
    return acc;
  }, {});
}

function createDefaultPinning<T>(columns: ColumnDef<T>[]) {
  return columns.reduce<Record<string, ColumnPin>>((acc, col) => {
    if (col.pinned && col.pinned !== 'none') {
      acc[col.id] = col.pinned;
    }
    return acc;
  }, {});
}

function normalizeSortDirection(direction?: SortDirection) {
  return direction === 'desc' ? 'desc' : 'asc';
}

export type RowItem<T> =
  | { type: 'section'; section: TableSection<T>; expanded: boolean }
  | { type: 'data'; row: T; rowId: string; sectionId?: string }
  | {
      type: 'detail';
      row: T;
      parentRow: T;
      parentRowId: string;
      subRowId: string;
      rowId: string;
      sectionId?: string;
      detailIndex: number;
    };

export type DataTableState<T> = {
  columns: ColumnDef<T>[];
  orderedColumns: ColumnDef<T>[];
  visibleColumns: ColumnDef<T>[];
  columnSizing: ColumnSizing;
  columnVisibility: ColumnVisibility;
  columnOrder: ColumnOrder;
  columnPinning: Record<string, ColumnPin>;
  page: number;
  pageSize: number;
  sort: TableSort;
  selectionMode: SelectionMode;
  selectedRowIds: string[];
  selectedSubRowIds: string[];
  expandedRowIds: string[];
  sectionExpandedIds: string[];
  getRowId: (row: T, index: number) => string;
  rowsToRender: T[];
  totalCount: number;
  rowItems: RowItem<T>[];
  setPage: (next: number) => void;
  setPageSize: (next: number) => void;
  setSort: (next: TableSort) => void;
  setSelectedRowIds: (next: string[]) => void;
  setSelectedSubRowIds: (next: string[]) => void;
  setColumnVisibility: (next: ColumnVisibility) => void;
  setColumnOrder: (next: ColumnOrder) => void;
  setColumnSizing: (next: ColumnSizing) => void;
  setColumnPinning: (next: Record<string, ColumnPin>) => void;
  setExpandedRowIds: (next: string[]) => void;
  setSectionExpandedIds: (next: string[]) => void;
  toggleRowSelection: (rowId: string) => void;
  toggleSubRowSelection: (rowId: string) => void;
  toggleAllRows: (rowIds: string[]) => void;
  toggleAllSubRows: (rowIds: string[]) => void;
  toggleRowExpanded: (rowId: string) => void;
  toggleSectionExpanded: (sectionId: string) => void;
  getColumnPin: (columnId: string) => ColumnPin;
};

/**
 * Internal state hook for `TableBaseImpl`. Prefer using `PaginatedTable`
 * or `InfiniteTable` instead of consuming this directly.
 *
 * * Centralized state/model logic for DataTable.
 *
 * * - Supports controlled and uncontrolled patterns for pagination, sorting,
 * *  selection, and column preferences.
 * * - Derives ordered/visible columns, pinned offsets, and render-ready row items
 * * (including sections and collapsible detail rows).
 * * - Emits change callbacks so higher-level managers can persist preferences.
 */
export function useTableBaseState<T>({
  columns,
  rows,
  getRowId,
  getSubRowId,
  paginationMode = 'classic',
  page,
  defaultPage = 0,
  pageSize,
  defaultPageSize = 10,
  totalRows,
  showPagination = true,
  onPageChange,
  onPageSizeChange,
  getRowSortPosition,
  sort,
  defaultSort = null,
  onSortChange,
  selectionMode = 'none',
  selectedRowIds,
  defaultSelectedRowIds = [],
  onSelectionChange,
  selectedSubRowIds,
  defaultSelectedSubRowIds = [],
  onSubRowSelectionChange,
  columnVisibility,
  defaultColumnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  defaultColumnOrder,
  onColumnOrderChange,
  columnSizing,
  defaultColumnSizing,
  onColumnSizingChange,
  columnPinning,
  defaultColumnPinning,
  onColumnPinningChange,
  sections,
  sectionExpandedIds,
  defaultSectionExpandedIds,
  onSectionExpandedChange,
  expandedRowIds: controlledExpandedRowIds,
  defaultExpandedRowIds,
  onExpandedRowIdsChange,
  collapsibleRows,
  renderRowSubRows,
  renderRowSubCells,
  renderRowSubComponent,
}: Pick<
  TableBaseImplProps<T>,
  | 'columns'
  | 'rows'
  | 'getRowId'
  | 'getSubRowId'
  | 'paginationMode'
  | 'page'
  | 'defaultPage'
  | 'pageSize'
  | 'defaultPageSize'
  | 'totalRows'
  | 'showPagination'
  | 'onPageChange'
  | 'onPageSizeChange'
  | 'getRowSortPosition'
  | 'sort'
  | 'defaultSort'
  | 'onSortChange'
  | 'selectionMode'
  | 'selectedRowIds'
  | 'defaultSelectedRowIds'
  | 'onSelectionChange'
  | 'selectedSubRowIds'
  | 'defaultSelectedSubRowIds'
  | 'onSubRowSelectionChange'
  | 'columnVisibility'
  | 'defaultColumnVisibility'
  | 'onColumnVisibilityChange'
  | 'columnOrder'
  | 'defaultColumnOrder'
  | 'onColumnOrderChange'
  | 'columnSizing'
  | 'defaultColumnSizing'
  | 'onColumnSizingChange'
  | 'columnPinning'
  | 'defaultColumnPinning'
  | 'onColumnPinningChange'
  | 'sections'
  | 'sectionExpandedIds'
  | 'defaultSectionExpandedIds'
  | 'onSectionExpandedChange'
  | 'expandedRowIds'
  | 'defaultExpandedRowIds'
  | 'onExpandedRowIdsChange'
  | 'renderRowSubRows'
  | 'renderRowSubCells'
  | 'renderRowSubComponent'
> & {
  collapsibleRows: boolean;
}) {
  const safeGetRowId = useCallback(
    (row: T, index: number) => (getRowId ? getRowId(row, index) : `${index}`),
    [getRowId],
  );

  const [currentPage, setPage] = useControlledState({
    value: page,
    defaultValue: defaultPage,
    onChange: onPageChange,
  });

  const [currentPageSize, setPageSize] = useControlledState({
    value: pageSize,
    defaultValue: defaultPageSize,
    onChange: onPageSizeChange,
  });

  const [currentSort, setSort] = useControlledState({
    value: sort,
    defaultValue: defaultSort,
    onChange: onSortChange,
  });

  const [currentSelection, setSelection] = useControlledState({
    value: selectedRowIds,
    defaultValue: defaultSelectedRowIds,
    onChange: onSelectionChange,
  });

  const [currentSubSelection, setSubSelection] = useControlledState({
    value: selectedSubRowIds,
    defaultValue: defaultSelectedSubRowIds,
    onChange: onSubRowSelectionChange,
  });

  const [currentVisibility, setVisibility] = useControlledState({
    value: columnVisibility,
    defaultValue: createDefaultVisibility(columns, defaultColumnVisibility),
    onChange: onColumnVisibilityChange,
  });

  const [currentOrder, setOrder] = useControlledState({
    value: columnOrder,
    defaultValue: defaultColumnOrder ?? columns.map((col) => col.id),
    onChange: onColumnOrderChange,
  });

  const [currentSizing, setSizing] = useControlledState({
    value: columnSizing,
    defaultValue: { ...createDefaultSizing(columns), ...defaultColumnSizing },
    onChange: onColumnSizingChange,
  });

  const [currentPinning, setPinning] = useControlledState({
    value: columnPinning,
    defaultValue: {
      ...createDefaultPinning(columns),
      ...(defaultColumnPinning ?? {}),
    },
    onChange: onColumnPinningChange,
  });

  const [expandedRowIds, setExpandedRowIds] = useControlledState({
    value: controlledExpandedRowIds,
    defaultValue: defaultExpandedRowIds ?? [],
    onChange: onExpandedRowIdsChange,
  });

  const [currentSectionExpanded, setSectionExpanded] = useControlledState({
    value: sectionExpandedIds,
    defaultValue:
      defaultSectionExpandedIds ??
      (sections ?? [])
        .filter((section) => section.defaultExpanded)
        .map((section) => section.id),
    onChange: onSectionExpandedChange,
  });

  const columnsById = useMemo(() => {
    return columns.reduce<Record<string, ColumnDef<T>>>((acc, col) => {
      acc[col.id] = col;
      return acc;
    }, {});
  }, [columns]);

  const orderedColumns = useMemo(
    () =>
      currentOrder
        .map((id) => columnsById[id])
        .filter((col) => Boolean(col)) as ColumnDef<T>[],
    [columnsById, currentOrder],
  );

  const visibleColumns = useMemo(
    () => orderedColumns.filter((col) => currentVisibility[col.id] !== false),
    [currentVisibility, orderedColumns],
  );

  const getColumnPin = useCallback(
    (columnId: string) =>
      currentPinning[columnId] ?? columnsById[columnId]?.pinned ?? 'none',
    [columnsById, currentPinning],
  );

  const pinnedLeft = useMemo(
    () => visibleColumns.filter((col) => getColumnPin(col.id) === 'left'),
    [getColumnPin, visibleColumns],
  );
  const pinnedRight = useMemo(
    () => visibleColumns.filter((col) => getColumnPin(col.id) === 'right'),
    [getColumnPin, visibleColumns],
  );
  const pinnedCenter = useMemo(
    () => visibleColumns.filter((col) => getColumnPin(col.id) === 'none'),
    [getColumnPin, visibleColumns],
  );

  const orderedVisibleColumns = useMemo(
    () => [...pinnedLeft, ...pinnedCenter, ...pinnedRight],
    [pinnedCenter, pinnedLeft, pinnedRight],
  );

  const sortedRows = useMemo(() => {
    if (!currentSort) {
      return rows;
    }

    const { columnId, direction } = currentSort;
    const column = columnsById[columnId];
    if (!column || column.sortable !== true) {
      return rows;
    }

    const sortDirection = normalizeSortDirection(direction);
    const compare =
      column.sortComparator ??
      ((a: T, b: T) => {
        const valueA = column.accessor?.(a);
        const valueB = column.accessor?.(b);
        if (valueA === valueB) {
          return 0;
        }
        if (valueA == null) {
          return 1;
        }
        if (valueB == null) {
          return -1;
        }
        if (valueA > valueB) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        if (valueA < valueB) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        return 0;
      });

    const positionRank = { first: 0, normal: 1, last: 2 } as const;

    return [...rows].sort((a, b) => {
      const positionA = getRowSortPosition?.(a) ?? 'normal';
      const positionB = getRowSortPosition?.(b) ?? 'normal';

      if (positionA !== positionB) {
        return positionRank[positionA] - positionRank[positionB];
      }

      if (positionA !== 'normal') {
        return 0;
      }

      return compare(a, b, sortDirection);
    });
  }, [columnsById, currentSort, getRowSortPosition, rows]);

  const totalCount = totalRows ?? sortedRows.length;

  const rowsToRender = useMemo(() => {
    if (paginationMode !== 'classic' || !showPagination) {
      return sortedRows;
    }

    if (totalRows !== undefined) {
      return sortedRows;
    }

    const start = currentPage * currentPageSize;
    return sortedRows.slice(start, start + currentPageSize);
  }, [
    currentPage,
    currentPageSize,
    paginationMode,
    showPagination,
    sortedRows,
    totalRows,
  ]);

  const rowItems = useMemo<RowItem<T>[]>(() => {
    const items: RowItem<T>[] = [];
    const activeSections = sections ?? [];
    const beforeSections = activeSections.filter(
      (section) => section.position !== 'after',
    );
    const afterSections = activeSections.filter(
      (section) => section.position === 'after',
    );

    const appendDetailRows = (row: T, rowId: string, sectionId?: string) => {
      const subRows =
        renderRowSubRows?.(row) ??
        (renderRowSubCells || renderRowSubComponent ? [row] : []);
      if (subRows.length === 0) {
        return;
      }
      subRows.forEach((subRow, index) => {
        const subRowId = getSubRowId
          ? getSubRowId(subRow, row, index, rowId)
          : ((subRow as { id?: string }).id ?? `${rowId}::sub-${index}`);
        items.push({
          type: 'detail',
          row: subRow,
          parentRow: row,
          parentRowId: rowId,
          subRowId,
          rowId: `${rowId}::detail-${index}`,
          sectionId,
          detailIndex: index,
        });
      });
    };

    const appendSection = (section: TableSection<T>) => {
      const expanded = currentSectionExpanded.includes(section.id);
      items.push({ type: 'section', section, expanded });
      if (expanded) {
        section.rows.forEach((row, rowIndex) => {
          const rowId = `${section.id}::${safeGetRowId(row, rowIndex)}`;
          items.push({ type: 'data', row, rowId, sectionId: section.id });
          if (collapsibleRows && expandedRowIds.includes(rowId)) {
            appendDetailRows(row, rowId, section.id);
          }
        });
      }
    };

    beforeSections.forEach(appendSection);

    rowsToRender.forEach((row, rowIndex) => {
      const rowId = safeGetRowId(row, rowIndex);
      items.push({ type: 'data', row, rowId });
      if (collapsibleRows && expandedRowIds.includes(rowId)) {
        appendDetailRows(row, rowId);
      }
    });

    afterSections.forEach(appendSection);

    return items;
  }, [
    getSubRowId,
    collapsibleRows,
    currentSectionExpanded,
    expandedRowIds,
    renderRowSubCells,
    renderRowSubComponent,
    renderRowSubRows,
    rowsToRender,
    safeGetRowId,
    sections,
  ]);

  const toggleRowSelection = useCallback(
    (rowId: string) => {
      if (selectionMode === 'none') {
        return;
      }

      if (selectionMode === 'single') {
        setSelection(currentSelection[0] === rowId ? [] : [rowId]);
        return;
      }

      const next = currentSelection.includes(rowId)
        ? currentSelection.filter((id) => id !== rowId)
        : [...currentSelection, rowId];
      setSelection(next);
    },
    [currentSelection, selectionMode, setSelection],
  );

  const toggleSubRowSelection = useCallback(
    (rowId: string) => {
      const next = currentSubSelection.includes(rowId)
        ? currentSubSelection.filter((id) => id !== rowId)
        : [...currentSubSelection, rowId];
      setSubSelection(next);
    },
    [currentSubSelection, setSubSelection],
  );

  const toggleAllRows = useCallback(
    (rowIds: string[]) => {
      if (selectionMode !== 'multiple') {
        return;
      }
      const allSelected = rowIds.every((id) => currentSelection.includes(id));
      setSelection(allSelected ? [] : rowIds);
    },
    [currentSelection, selectionMode, setSelection],
  );

  const toggleAllSubRows = useCallback(
    (rowIds: string[]) => {
      if (rowIds.length === 0) {
        return;
      }
      const allSelected = rowIds.every((id) =>
        currentSubSelection.includes(id),
      );
      const next = allSelected
        ? currentSubSelection.filter((id) => !rowIds.includes(id))
        : Array.from(new Set([...currentSubSelection, ...rowIds]));
      setSubSelection(next);
    },
    [currentSubSelection, setSubSelection],
  );

  const toggleRowExpanded = useCallback(
    (rowId: string) => {
      const next = expandedRowIds.includes(rowId)
        ? expandedRowIds.filter((id) => id !== rowId)
        : [...expandedRowIds, rowId];

      setExpandedRowIds(next);
    },
    [expandedRowIds, setExpandedRowIds],
  );

  const toggleSectionExpanded = useCallback(
    (sectionId: string) => {
      const next = currentSectionExpanded.includes(sectionId)
        ? currentSectionExpanded.filter((id) => id !== sectionId)
        : [...currentSectionExpanded, sectionId];
      setSectionExpanded(next);
    },
    [currentSectionExpanded, setSectionExpanded],
  );

  return {
    columns,
    orderedColumns,
    visibleColumns: orderedVisibleColumns,
    columnSizing: currentSizing,
    columnVisibility: currentVisibility,
    columnOrder: currentOrder,
    columnPinning: currentPinning,
    page: currentPage,
    pageSize: currentPageSize,
    sort: currentSort,
    selectionMode,
    selectedRowIds: currentSelection,
    selectedSubRowIds: currentSubSelection,
    expandedRowIds,
    sectionExpandedIds: currentSectionExpanded,
    getRowId: safeGetRowId,
    rowsToRender,
    totalCount,
    rowItems,
    setPage,
    setPageSize,
    setSort,
    setSelectedRowIds: setSelection,
    setSelectedSubRowIds: setSubSelection,
    setColumnVisibility: setVisibility,
    setColumnOrder: setOrder,
    setColumnSizing: setSizing,
    setColumnPinning: setPinning,
    setExpandedRowIds,
    setSectionExpandedIds: setSectionExpanded,
    toggleRowSelection,
    toggleSubRowSelection,
    toggleAllRows,
    toggleAllSubRows,
    toggleRowExpanded,
    toggleSectionExpanded,
    getColumnPin,
  };
}
